import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { subDays } from "date-fns/subDays";
import { subMonths } from "date-fns/subMonths";
import { subWeeks } from "date-fns/subWeeks";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { Suspense, useEffect, useMemo, useState, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import * as RechartsPrimitive from "recharts";
import z from "zod";
import { Icon } from "@/components/icon";
import { LessonAnalyticsTable } from "@/components/tables/lesson-analytics";
import { CardTitle } from "@/components/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { AiActiveMembersChart } from "@/ui/analytics/ai/ai-active-members-chart";
import { AiMessagesChart } from "@/ui/analytics/ai/ai-messages-chart";
import { AiThreadsChart } from "@/ui/analytics/ai/ai-threads-chart";
import { ActiveMembersChart } from "@/ui/analytics/community/active-members-chart";
import { MemberChart } from "@/ui/analytics/community/member-chart";
import { PostsChart } from "@/ui/analytics/community/posts-chart";
import { PostsPerCommentChart } from "@/ui/analytics/community/posts-per-comment-chart";
import type { analyticsAiQuery } from "./__generated__/analyticsAiQuery.graphql";
import type { analyticsCommunityAnalyticsQuery } from "./__generated__/analyticsCommunityAnalyticsQuery.graphql";
import type { analyticsCourse_lessons$key } from "./__generated__/analyticsCourse_lessons.graphql";
import type { analyticsCourseQuery } from "./__generated__/analyticsCourseQuery.graphql";
import type { analyticsQuery } from "./__generated__/analyticsQuery.graphql";
import type { CourseAnalyticsTableQuery } from "./__generated__/CourseAnalyticsTableQuery.graphql";

function AnalyticsSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-4 gap-6">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i} className="flex flex-col">
						<Separator />
						<Skeleton className="h-4 w-20 mt-3" />
						<Skeleton className="h-10 w-24 mt-1.5" />
					</div>
				))}
			</div>
			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<Skeleton className="h-5 w-40 mx-6 mb-6" />
				<Skeleton className="h-[350px] mx-6" />
			</div>
			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<Skeleton className="h-5 w-48 mx-6 mb-6" />
				<Skeleton className="h-[350px] mx-6" />
			</div>
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<div className="overflow-hidden rounded-md border pt-6 pb-3">
					<Skeleton className="h-5 w-32 mx-6 mb-6" />
					<Skeleton className="h-[300px] mx-6" />
				</div>
				<div className="overflow-hidden rounded-md border pt-6 pb-3">
					<Skeleton className="h-5 w-36 mx-6 mb-6" />
					<Skeleton className="h-[300px] mx-6" />
				</div>
			</div>
		</div>
	);
}

function CourseAnalyticsSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-4 gap-6">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i} className="flex flex-col">
						<Separator />
						<Skeleton className="h-4 w-28 mt-3" />
						<Skeleton className="h-10 w-20 mt-1.5" />
					</div>
				))}
			</div>
			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<Skeleton className="h-5 w-48 mx-6 mb-6" />
				<Skeleton className="h-[350px] mx-6" />
			</div>
			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<Skeleton className="h-5 w-40 mx-6 mb-6" />
				<Skeleton className="h-[350px] mx-6" />
			</div>
			<div className="rounded-md border">
				<div className="p-4 border-b">
					<Skeleton className="h-5 w-32" />
				</div>
				{Array.from({ length: 5 }).map((_, i) => (
					<div
						key={i}
						className="flex items-center gap-4 p-4 border-b last:border-b-0"
					>
						<Skeleton className="h-4 flex-1" />
						<Skeleton className="h-4 w-20" />
						<Skeleton className="h-4 w-16" />
						<Skeleton className="h-4 w-24" />
					</div>
				))}
			</div>
		</div>
	);
}

const defaultValues = {
	view: "comunidade",
	periodo: "7",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/analytics/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["comunidade", "cursos", "ia"]).default("comunidade"),
			curso: z.string().optional(),
			periodo: z.enum(["7", "15", "28", "90", "365"]).default("7"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function CommunityAnalytics() {
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<analyticsCommunityAnalyticsQuery>(
		graphql`
		query analyticsCommunityAnalyticsQuery(
		  $slug: String!
			$memberCountPeriod: StatPeriod!
			$memberCountLast: Int!
			$postsPeriod: StatPeriod!
			$postsLast: Int!
			$postsPerCommentsPeriod: StatPeriod!
		) {
			club(slug: $slug) {
			  ...memberChartCountTimeseries @arguments(last: $memberCountLast, period: $memberCountPeriod)
				...postsChartTimeseries @arguments(last: $postsLast, period: $postsPeriod)
				...postsPerCommentChartTimeseries @arguments(period: $postsPerCommentsPeriod)

				analytics {
				  ...activeMembersChart

				  weekMemberCount: memberCount {
						timeseries(last: 7, period: DAILY) {
							count
							date
						}
						aggregate {
							count
						}
					}
					postCount {
						aggregate {
							total
						}
					}
					activeUsers {
    		    timeseries {
        			date
        			daily
        			monthly
     			  }
      		}
				}
			}
		}
	`,
		{
			slug: club?.slug,
			memberCountPeriod: "DAILY",
			memberCountLast: 180,
			postsPeriod: "DAILY",
			postsLast: 180,
			postsPerCommentsPeriod: "DAILY",
		},
	);

	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-4 gap-6">
				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">Membros</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.analytics.weekMemberCount.aggregate.count}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">Posts</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.analytics.postCount.aggregate.total}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Novos Membros (7d)
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.analytics.weekMemberCount.timeseries.length > 1
							? data.club.analytics.weekMemberCount.timeseries[
									data.club.analytics.weekMemberCount.timeseries.length - 1
								].count -
									data.club.analytics.weekMemberCount.timeseries[
										data.club.analytics.weekMemberCount.timeseries.length -
											1 -
											6
									].count ||
								data.club.analytics.weekMemberCount.timeseries[0].count
							: "-"}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3 inline-flex items-center justify-start gap-1.5">
						Stickness
						<Tooltip>
							<TooltipTrigger>
								<Icon name="Info" className="size-3 " />
							</TooltipTrigger>

							<TooltipContent>
								Stickness indica o quão engajado está sua comunidade. Indica a
								porcentagem dos membros mensais retornam diariamente.
							</TooltipContent>
						</Tooltip>
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.analytics.activeUsers.timeseries.length >= 1 &&
						data.club.analytics.activeUsers.timeseries[
							data.club.analytics.activeUsers.timeseries.length - 1
						].monthly > 0
							? (
									data.club.analytics.activeUsers.timeseries[
										data.club.analytics.activeUsers.timeseries.length - 1
									].daily /
									data.club.analytics.activeUsers.timeseries[
										data.club.analytics.activeUsers.timeseries.length - 1
									].monthly
								).toLocaleString("pt-BR", {
									style: "percent",
									maximumFractionDigits: 1,
								})
							: "-"}
					</span>
				</div>
			</div>

			<MemberChart club={data.club} />

			<ActiveMembersChart analytics={data.club.analytics} />

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<PostsChart club={data.club} />

				<PostsPerCommentChart club={data.club} />
			</div>
		</div>
	);
}

function CourseAnalytics({ defaultCourse }: { defaultCourse: string }) {
	const { club } = useRouteContext({ from: Route.id });
	const { curso } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();

	const [page, setPage] = useState(1);

	const data = useLazyLoadQuery<analyticsCourseQuery>(
		graphql`
		query analyticsCourseQuery($slug: String!, $course: String!, $last: Int!, $period: StatPeriod!) {
		  club(slug: $slug) {
		    course(slug: $course) {
					analytics {
					  averageConclusionTime
            totalSubscribers
            conclusionPercentage
            totalInactiveSubscribers
            conclusionsTimeseries(last: $last, period: $period) {
              count
              date
            }
            enrollmentsTimeseries(last: $last, period: $period) {
              count
              date
            }
            inactiveSubscribersTimeseries(last: $last, period: $period) {
              date
              count
            }
					}

					...analyticsCourse_lessons @arguments(first: 10)
				}
		  }
		}
    `,
		{
			slug: club.slug,
			course: curso || defaultCourse,
			last: 10,
			period: "MONTHLY",
		},
	);

	const [lessons, refetch] = useRefetchableFragment<
		CourseAnalyticsTableQuery,
		analyticsCourse_lessons$key
	>(
		graphql`
      fragment analyticsCourse_lessons on Course
      @refetchable(queryName: "CourseAnalyticsTableQuery")
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
      )
      {
        lessons: courseClasses(status: ALL, first: $first, after: $after) {
					edges {
					  node {
							id
              name

              ...on CourseContent {
                  section {
                    name
                  }
                  videoViews
                  commentCount
              }
              completionPercentage
					  }
						cursor
					}
					pageInfo {
      			hasNextPage
  					hasPreviousPage
  					totalCount
  					startCursor
  					endCursor
					}
				}
      }
  `,
		data.club.course,
	);

	useEffect(() => {
		refetch(
			{
				first: 15,
				after: page === 1 ? null : offsetToCursor((page - 1) * 15 - 1),
			},
			{ fetchPolicy: "store-or-network" },
		);
	}, [page, refetch]);

	const chartConfig = {
		enrollments: {
			label: "Inscritos",
			color: "oklch(0.6989 0.1948 35.5697)",
		},
		conclusions: {
			label: "Conclusões",
			color: "oklch(0.4359 0.0945 162.0196)",
		},
		inactiveCount: {
			label: "Inativos",
			color: "oklch(0.4359 0.0945 162.0196)",
		},
	} satisfies ChartConfig;

	const combinedChartData = useMemo(() => {
		const enrollmentsMap = new Map(
			data.club.course.analytics.enrollmentsTimeseries.map((item) => [
				item.date,
				item.count,
			]),
		);
		const conclusionsMap = new Map(
			data.club.course.analytics.conclusionsTimeseries.map((item) => [
				item.date,
				item.count,
			]),
		);

		const allDates = new Set([
			...data.club.course.analytics.enrollmentsTimeseries.map(
				(item) => item.date,
			),
			...data.club.course.analytics.conclusionsTimeseries.map(
				(item) => item.date,
			),
		]);

		return Array.from(allDates)
			.sort()
			.map((date) => ({
				date,
				enrollments: enrollmentsMap.get(date) || 0,
				conclusions: conclusionsMap.get(date) || 0,
			}));
	}, [data.club.course.analytics]);

	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-4 gap-6">
				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Total de Alunos
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.course.analytics.totalSubscribers}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						% de conclusão
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{(
							(data.club.course.analytics.conclusionPercentage || 0) / 100
						).toLocaleString("pt-BR", {
							style: "percent",
							maximumFractionDigits: 1,
						})}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Tempo Médio para Conclusão
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.course.analytics.averageConclusionTime}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Alunos Inativos
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.course.analytics.totalInactiveSubscribers}
					</span>
				</div>
			</div>

			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<CardTitle className="px-6 mb-6">Inscrições e Conclusões</CardTitle>
				{combinedChartData.length === 0 ||
				!combinedChartData.some(
					(c) => c.conclusions > 0 || c.enrollments > 0,
				) ? (
					<div className="flex h-[350px] items-center justify-center px-6">
						<span className="text-muted-foreground">
							Informação ainda não disponível
						</span>
					</div>
				) : (
					<ChartContainer
						config={chartConfig}
						className="pr-6 max-h-[350px] w-full"
					>
						<RechartsPrimitive.ComposedChart data={combinedChartData}>
							<RechartsPrimitive.CartesianGrid vertical={false} />
							<RechartsPrimitive.XAxis
								dataKey="date"
								tickLine={false}
								axisLine={false}
								tickMargin={10}
								tickFormatter={(value) =>
									new Date(value).toLocaleDateString("pt-BR", {
										day: "2-digit",
										month: "2-digit",
									})
								}
							/>
							<RechartsPrimitive.YAxis
								tickLine={false}
								axisLine={false}
								tick={false}
								width={0}
							/>
							<ChartTooltip
								content={
									<ChartTooltipContent
										labelFormatter={(value) =>
											new Date(value).toLocaleDateString("pt-BR", {
												day: "2-digit",
												month: "2-digit",
											})
										}
									/>
								}
							/>
							<RechartsPrimitive.Bar
								dataKey="conclusions"
								fill={chartConfig.conclusions.color}
							/>
							<RechartsPrimitive.Line
								dataKey="enrollments"
								stroke={chartConfig.enrollments.color}
								strokeWidth={2}
								dot={false}
							/>
						</RechartsPrimitive.ComposedChart>
					</ChartContainer>
				)}
			</div>

			<div className="overflow-hidden rounded-md border pt-6 pb-3">
				<CardTitle className="px-6 mb-6">Membros Inativos</CardTitle>
				{!data.club.course.analytics.inactiveSubscribersTimeseries ||
				data.club.course.analytics.inactiveSubscribersTimeseries.length === 0 ||
				!data.club.course.analytics.inactiveSubscribersTimeseries.some(
					(t) => t.count,
				) ? (
					<div className="flex h-[350px] items-center justify-center px-6">
						<span className="text-muted-foreground">
							Informação ainda não disponível
						</span>
					</div>
				) : (
					<ChartContainer
						config={chartConfig}
						className="pr-6 max-h-[350px] w-full"
					>
						<RechartsPrimitive.LineChart
							data={[
								...(data.club.course.analytics.inactiveSubscribersTimeseries ||
									[]),
							]}
						>
							<RechartsPrimitive.CartesianGrid vertical={false} />
							<RechartsPrimitive.XAxis
								dataKey="date"
								tickLine={false}
								axisLine={false}
								tickMargin={10}
								tickFormatter={(value) =>
									new Date(value).toLocaleDateString("pt-BR", {
										day: "2-digit",
										month: "2-digit",
									})
								}
							/>
							<RechartsPrimitive.YAxis
								tickLine={false}
								axisLine={false}
								tick={false}
								width={0}
							/>
							<ChartTooltip
								content={
									<ChartTooltipContent
										labelFormatter={(value) =>
											new Date(value).toLocaleDateString("pt-BR", {
												day: "2-digit",
												month: "2-digit",
											})
										}
									/>
								}
							/>
							<RechartsPrimitive.Line
								dataKey="count"
								name="inactiveCount"
								stroke={chartConfig.inactiveCount.color}
								strokeWidth={2}
								dot={false}
							/>
						</RechartsPrimitive.LineChart>
					</ChartContainer>
				)}
			</div>

			<div>
				<LessonAnalyticsTable
					data={
						lessons?.lessons?.edges.filter(Boolean).map(({ node }) => ({
							id: node.id,
							section: node.section?.name ?? "",
							name: node.name,
							videoViews: node.videoViews || undefined,
							commentCount: node.commentCount || undefined,
							completionPercentage: node.completionPercentage
								? node.completionPercentage / 100
								: 0,
						})) || []
					}
					isPending={isPending}
					totalCount={lessons?.lessons.pageInfo.totalCount || 0}
					hasNextPage={Boolean(lessons?.lessons.pageInfo.hasNextPage)}
					hasPreviousPage={Boolean(lessons?.lessons.pageInfo.hasPreviousPage)}
					loadNext={() =>
						startTransition(() => {
							setPage((p) => p + 1);
						})
					}
					loadPrevious={() =>
						startTransition(() => {
							setPage((p) => p - 1);
						})
					}
					startIndex={
						lessons?.lessons.edges[0]
							? cursorToOffset(lessons?.lessons.edges[0].cursor) + 1
							: 1
					}
					endIndex={
						lessons?.lessons.edges[lessons?.lessons.edges.length - 1]
							? cursorToOffset(
									lessons?.lessons.edges[lessons?.lessons.edges.length - 1]
										.cursor,
								) + 1
							: 1
					}
				/>
			</div>
		</div>
	);
}

function AiAnalytics() {
	const { club } = useRouteContext({ from: Route.id });
	const { periodo } = useSearch({ from: Route.id });

	const queryParams = useMemo(() => {
		const endDate = new Date();

		switch (periodo) {
			case "7":
				return {
					interval: "DAILY" as const,
					limit: 7 as const,
					startDate: subDays(endDate, 7),
					endDate,
				};
			case "15":
				return {
					interval: "DAILY" as const,
					limit: 15 as const,
					startDate: subDays(endDate, 15),
					endDate,
				};
			case "28":
				return {
					interval: "DAILY" as const,
					limit: 28 as const,
					startDate: subDays(endDate, 28),
					endDate,
				};
			case "90":
				return {
					interval: "WEEKLY" as const,
					limit: 12 as const,
					startDate: subWeeks(endDate, 12),
					endDate,
				};
			case "365":
				return {
					interval: "MONTHLY" as const,
					limit: 12 as const,
					startDate: subMonths(endDate, 12),
					endDate,
				};
		}
	}, [periodo]);

	const data = useLazyLoadQuery<analyticsAiQuery>(
		graphql`
		query analyticsAiQuery(
	    $slug: String!
			$startDate: DateTime!,
      $endDate: DateTime!,
      $interval: StatPeriod!,
      $limit: Int!
		) {
		  club(slug: $slug) {
				memberCount
		    aiPersona {
          analytics {
            messages {
              aggregate(startDate: $startDate, endDate: $endDate, interval: $interval) {
                total
              }
              timeseries(startDate: $startDate, endDate: $endDate, interval: $interval, limit: $limit) {
                date
                count
              }
            }
            threads {
              aggregate(startDate: $startDate, endDate: $endDate, interval: $interval) {
                total
              }
              timeseries(startDate: $startDate, endDate: $endDate, interval: $interval, limit: $limit) {
                date
                count
              }
            }
            activeUsers {
              aggregate(startDate: $startDate, endDate: $endDate, interval: $interval) {
                total
              }
            }

            ...aiActiveMembersChartData @arguments(startDate: $startDate, endDate: $endDate, interval: $interval, limit: $limit)
            ...aiThreadsChartData @arguments(startDate: $startDate, endDate: $endDate, interval: $interval, limit: $limit)
            ...aiMessagesChartData @arguments(startDate: $startDate, endDate: $endDate, interval: $interval, limit: $limit)
          }
        }
		  }
		}
   `,
		{ slug: club.slug, ...queryParams },
	);

	return (
		<div className="flex flex-col gap-6">
			<div className="grid grid-cols-4 gap-6">
				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Mensagens Enviadas
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.aiPersona?.analytics?.messages.aggregate.total || 0}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Alunos Engajados
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.aiPersona?.analytics?.activeUsers.aggregate.total || 0}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Perguntas por Alunos
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{data.club.aiPersona?.analytics?.activeUsers.aggregate.total
							? (data.club.aiPersona?.analytics?.messages.aggregate.total ||
									0) /
								data.club.aiPersona?.analytics?.activeUsers.aggregate.total
							: 0}
					</span>
				</div>

				<div className="flex flex-col">
					<Separator />
					<span className="text-muted-foreground text-sm mt-3">
						Taxa de Utilização
					</span>

					<span className="font-semibold text-4xl mt-1.5">
						{(
							(data.club.aiPersona?.analytics?.activeUsers.aggregate.total ||
								0) / data.club.memberCount
						).toLocaleString("pt-BR", {
							minimumFractionDigits: 1,
							maximumFractionDigits: 1,
							style: "percent",
						})}
					</span>
				</div>
			</div>

			<AiActiveMembersChart
				analytics={data.club.aiPersona?.analytics || undefined}
			/>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				<AiMessagesChart
					analytics={data.club.aiPersona?.analytics || undefined}
				/>

				<AiThreadsChart
					analytics={data.club.aiPersona?.analytics || undefined}
				/>
			</div>
		</div>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { view, curso, periodo } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [_isPending, startTransition] = useTransition();

	const data = useLazyLoadQuery<analyticsQuery>(
		graphql`
    query analyticsQuery($slug: String!) {
        club(slug: $slug) {
          coursePage {
            categories(status: ALL) {
              id
              name
              position
              courses(status: ALL) {
                slug
                name
              }
            }
          }
        }
    }
  `,
		{ slug: club.slug },
	);

	const sortedCourseCategories = [...data.club.coursePage.categories].sort(
		(a, b) => (a.position ?? 0) - (b.position ?? 0),
	);
	const firstCourseSlug = sortedCourseCategories
		.flatMap((cat) => cat.courses)
		.at(0)?.slug;

	return (
		<div className="p-6">
			<Tabs
				defaultValue="comunidade"
				value={view}
				className="flex flex-col gap-6"
				onValueChange={(val) =>
					startTransition(() => {
						navigate({
							search: (s) => ({
								...s,
								view: val as "comunidade" | "cursos" | "ia",
							}),
						});
					})
				}
			>
				<div className="flex flex-row items-center justify-between">
					<TabsList>
						<TabsTrigger value="comunidade" className="border-none">
							Comunidade
						</TabsTrigger>
						<TabsTrigger value="cursos" className="border-none">
							Cursos
						</TabsTrigger>
						<TabsTrigger value="ia" className="border-none">
							IA
						</TabsTrigger>
					</TabsList>

					{view === "cursos" && firstCourseSlug && (
						<div>
							<Select
								name="curso"
								value={curso || firstCourseSlug}
								onValueChange={(e) =>
									startTransition(() => {
										navigate({ search: (s) => ({ ...s, curso: e }) });
									})
								}
							>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>

								<SelectContent>
									{sortedCourseCategories.map((cat) => (
										<SelectGroup key={cat.id}>
											<SelectLabel>{cat.name}</SelectLabel>

											{cat.courses.map((c) => (
												<SelectItem key={c.slug} value={c.slug}>
													{c.name}
												</SelectItem>
											))}
										</SelectGroup>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{view === "ia" && (
						<div>
							<Select
								name="period"
								value={periodo}
								onValueChange={(e: "7" | "15" | "28" | "90" | "365") =>
									startTransition(() => {
										navigate({ search: (s) => ({ ...s, periodo: e }) });
									})
								}
							>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>

								<SelectContent>
									<SelectItem value="7">7 dias</SelectItem>
									<SelectItem value="15">15 dias</SelectItem>
									<SelectItem value="28">28 dias</SelectItem>
									<SelectItem value="90">3 meses</SelectItem>
									<SelectItem value="365">12 meses</SelectItem>
								</SelectContent>
							</Select>
						</div>
					)}
				</div>

				<TabsContent value="comunidade">
					<Suspense fallback={<AnalyticsSkeleton />}>
						<CommunityAnalytics />
					</Suspense>
				</TabsContent>
				<TabsContent value="cursos">
					{firstCourseSlug ? (
						<Suspense fallback={<CourseAnalyticsSkeleton />}>
							<CourseAnalytics defaultCourse={firstCourseSlug} />
						</Suspense>
					) : (
						<div className="flex flex-col items-center justify-center py-24 gap-3">
							<Icon name="BookOpen" className="size-10 text-muted-foreground" />
							<p className="text-muted-foreground text-sm">
								Nenhum curso encontrado nesta comunidade.
							</p>
						</div>
					)}
				</TabsContent>
				<TabsContent value="ia">
					<Suspense fallback={<AnalyticsSkeleton />}>
						<AiAnalytics />
					</Suspense>
				</TabsContent>
			</Tabs>
		</div>
	);
}
